import React from 'react'
import { HelpDescription, Subtitle, SubtitleForm, TitleForm, TypeOptionsBlock } from './styles'
import { getResult, yesOrNoOptions } from './helper'

export type AccountabilityMatrixQuestionFlowVariant = 'individual' | 'group'

export type AccountabilityMatrixQuestionFlowProps = {
    variant?: AccountabilityMatrixQuestionFlowVariant
    question01: string
    question02: string
    question03: string
    question04: string
    question05: string
    question06: string
    question07: string
    question08: string
    question09: string
    question10: string
    result: string
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export const AccountabilityMatrixQuestionFlow: React.FC<AccountabilityMatrixQuestionFlowProps> = ({
    variant = 'individual',
    question01,
    question02,
    question03,
    question04,
    question05,
    question06,
    question07,
    question08,
    question09,
    question10,
    result,
    handleChange
}) => {
    const isGroup = variant === 'group'
    /** Sobrescreve larguras fixas (~1120px) dos styled components para caber na coluna ao lado do sidebar */
    const introWidthClass = '!max-w-full !w-full min-w-0'
    const questionRowClassName = isGroup
        ? 'flex min-w-0 flex-row flex-wrap items-start gap-x-4 gap-y-3'
        : 'flex min-w-0 flex-row'

    const innerClassName = isGroup
        ? 'w-full px-0 mt-6 mb-10 max-w-full min-w-0 overflow-x-hidden'
        : 'w-full px-8 mt-6 mb-10 min-w-0 max-w-full overflow-x-hidden'

    return (
        <div className={innerClassName}>
            <div className="flex flex-col min-w-0 max-w-full">
                <TitleForm>
                    {isGroup ? 'Matriz de responsabilização em massa' : 'Matriz de responsabilização'}
                </TitleForm>
                <Subtitle className={introWidthClass}>
                    A Matriz de Responsabilização será utilizada na análise de incidentes quando existir necessidade de definir e formalizar responsabilidades entre
                    profissionais, equipes, setores ou áreas de apoio, especialmente em eventos que envolvam falhas de processo, comunicação ou interfaces assistenciais.
                </Subtitle>
                <HelpDescription className={introWidthClass}>
                    <p className="mb-2 break-words">Conforme a POLDOR 007, sua aplicação é indicada: </p>
                    <div className="w-full min-w-0 max-w-full">
                        <ul className="list-disc list-outside pl-5 marker:font-bold marker:text-lg space-y-2 leading-relaxed break-words [overflow-wrap:anywhere]">
                            <li className="min-w-0">
                                Em notificações classificadas como circunstância notificável, near miss, incidente sem dano, com dano leve ou incidente com dano moderado, quando a Matriz de Risco apontar maior complexidade organizacional (pontuação maior que 5)
                            </li>
                            <li className="min-w-0">
                                Em eventos com dano grave, óbito, evento sentinela/never event ou risco de imagem é obrigatório.
                            </li>
                        </ul>
                    </div>
                </HelpDescription>
            </div>
            <div className={questionRowClassName}>
                <div className="w-[202px] flex justify-start items-baseline gap-3 shrink-0">
                    <TypeOptionsBlock>Teste do ato deliberado</TypeOptionsBlock>
                </div>
                <div data-testid="test-toggle-button" className="mt-7 block min-h-[1.5rem] pl-[1.5rem] min-w-0 flex-1">
                    <div className="z-20">
                        <legend className="font-semibold text-xs mb-3">
                            A ação foi intencional?
                        </legend>
                        <div
                            className='flex items-center'
                            data-testid="test-u_question_01-container"
                        >
                            {yesOrNoOptions.map((item) => (
                                <div
                                    key={`u_question_01-${item.id}`}
                                    className="mr-2 mb-3">
                                    <input name="u_question_01"
                                        id={item.id}
                                        checked={item.value === question01}
                                        type="radio"
                                        onChange={handleChange}
                                        value={item.value}
                                        className="h-[16px] w-[16px] border-gray-300 text-yellow focus:ring-0 focus:ring-offset-0 mr-1" />
                                    <span className="font-bold">{item.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {question01 && question01 === "yes" && (
                        <div className="z-20">
                            <legend className="font-semibold text-xs mb-3">
                                As consequências do ato foram intencionais?
                            </legend>
                            <div
                                data-testid="test-u_question_02-container"
                                className='flex items-center'>
                                {yesOrNoOptions.map((item) => (
                                    <div
                                        key={`u_question_02-${item.id}`}
                                        className="mr-2 mb-3">
                                        <input
                                            name="u_question_02"
                                            id={item.id}
                                            checked={item.value === question02}
                                            type="radio"
                                            onChange={handleChange}
                                            value={item.value}
                                            className="h-[16px] w-[16px] border-gray-300 text-yellow focus:ring-0 focus:ring-offset-0 mr-1" />
                                        <span className="font-bold">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {((question01 && question01 === "no") || (question02 && question02 === "no")) && (
                <div className={questionRowClassName}>
                    <div className="w-[202px] flex justify-start items-baseline gap-3 shrink-0">
                        <TypeOptionsBlock>Teste de incapacidade</TypeOptionsBlock>
                    </div>
                    <div className="mt-7 block min-h-[1.5rem] pl-[1.5rem] min-w-0 flex-1">
                        <div className="z-20">
                            <legend className="font-semibold text-xs mb-3">
                                O indivíduo está doente ou sob o uso de substâncias?
                            </legend>
                            <div
                                data-testid="test-u_question_03-container"
                                className='flex items-center'>
                                {yesOrNoOptions.map((item) => (
                                    <div key={`u_question_03-${item.id}`} className="mr-2 mb-3">
                                        <input key="u_question_03"
                                            name="u_question_03"
                                            type="radio"
                                            checked={item.value === question03}
                                            onChange={handleChange}
                                            value={item.value}
                                            className="h-[16px] w-[16px] border-gray-300 text-yellow focus:ring-0 focus:ring-offset-0 mr-1" />
                                        <span className="font-bold">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {question03 && question03 === "yes" && (
                            <div className="z-20">
                                <legend className="font-semibold text-xs mb-3">
                                    O uso de substâncias é justificável?
                                </legend>
                                <div
                                    data-testid="test-u_question_04-container"
                                    className='flex items-center'>
                                    {yesOrNoOptions.map((item) => (
                                        <div key={`u_question_04-${item.id}`} className="mr-2 mb-3">
                                            <input key="u_question_04"
                                                name="u_question_04"
                                                type="radio"
                                                checked={item.value === question04}
                                                onChange={handleChange}
                                                value={item.value}
                                                className="h-[16px] w-[16px] border-gray-300 text-yellow focus:ring-0 focus:ring-offset-0 mr-1" />
                                            <span className="font-bold">{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {((question03 && question03 === "no") || (question04 && question04 === "yes")) && (
                <div className={questionRowClassName}>
                    <div className="w-[202px] flex justify-start items-baseline gap-3 shrink-0">
                        <TypeOptionsBlock>Teste de "visão”</TypeOptionsBlock>
                    </div>
                    <div data-testid="test-toggle-button" className="mt-7 block min-h-[1.5rem] pl-[1.5rem] min-w-0 flex-1">
                        <div className="z-20">
                            <legend className="font-semibold text-xs mb-3">
                                Havia um documento descrito que padronizasse a rotina (exemplo: POP, protocolo)?
                            </legend>
                            <div
                                data-testid="test-u_question_05-container"
                                className='flex items-center'>
                                {yesOrNoOptions.map((item) => (
                                    <div key={`u_question_05-${item.id}`} className="mr-2 mb-3">
                                        <input key="u_question_05"
                                            name="u_question_05"
                                            type="radio"
                                            checked={item.value === question05}
                                            onChange={handleChange}
                                            value={item.value}
                                            className="h-[16px] w-[16px] border-gray-300 text-yellow focus:ring-0 focus:ring-offset-0 mr-1" />
                                        <span className="font-bold">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {question05 && question05 === "yes" && (
                            <div className="z-20">
                                <legend className="font-semibold text-xs mb-3">
                                    Este documento estava disponível e de forma inteligível (fácil de compreender)?
                                </legend>
                                <div
                                    data-testid="test-u_question_06-container"
                                    className='flex items-center'>
                                    {yesOrNoOptions.map((item) => (
                                        <div key={`u_question_06-${item.id}`} className="mr-2 mb-3">
                                            <input key="u_question_06"
                                                name="u_question_06"
                                                type="radio"
                                                checked={item.value === question06}
                                                onChange={handleChange}
                                                value={item.value}
                                                className="h-[16px] w-[16px] border-gray-300 text-yellow focus:ring-0 focus:ring-offset-0 mr-1" />
                                            <span className="font-bold">{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {question06 && question06 === "yes" && (
                            <div className="z-20">
                                <legend className="font-semibold text-xs mb-3">
                                    Há evidencias de violação consciente do procedimento de segurança?
                                </legend>
                                <div
                                    data-testid="test-u_question_07-container"
                                    className='flex items-center'>
                                    {yesOrNoOptions.map((item) => (
                                        <div key={`u_question_07-${item.id}`} className="mr-2 mb-3">
                                            <input key="u_question_07"
                                                name="u_question_07"
                                                type="radio"
                                                checked={item.value === question07}
                                                onChange={handleChange}
                                                value={item.value}
                                                className="h-[16px] w-[16px] border-gray-300 text-yellow focus:ring-0 focus:ring-offset-0 mr-1" />
                                            <span className="font-bold">{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            )}

            {((question05 && question05 === "no") || (question06 && question06 === "no")) && (
                <div className={questionRowClassName}>
                    <div className="w-[202px] flex justify-start items-baseline gap-3 shrink-0">
                        <TypeOptionsBlock>Teste de substituição</TypeOptionsBlock>
                    </div>
                    <div data-testid="test-toggle-button" className="mt-7 block min-h-[1.5rem] pl-[1.5rem] min-w-0 flex-1">
                        <div className="z-20">
                            <legend className="font-semibold text-xs mb-3">
                                Outro profissional com o mesmo cargo faria a mesma coisa?
                            </legend>
                            <div
                                data-testid="test-u_question_08-container"
                                className='flex items-center'>
                                {yesOrNoOptions.map((item) => (
                                    <div key={`u_question_08-${item.id}`} className="mr-2 mb-3">
                                        <input key="u_question_08"
                                            name="u_question_08"
                                            type="radio"
                                            checked={item.value === question08}
                                            onChange={handleChange}
                                            value={item.value}
                                            className="h-[16px] w-[16px] border-gray-300 text-yellow focus:ring-0 focus:ring-offset-0 mr-1" />
                                        <span className="font-bold">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {question08 && question08 === "no" && (
                            <div className="z-20">
                                <legend className="font-semibold text-xs mb-3">
                                    Há falhas de treinamento do indivíduo ou ele era inexperiente?
                                </legend>
                                <div className='flex items-center'>
                                    {yesOrNoOptions.map((item) => (
                                        <div key={`u_question_09-${item.id}`} className="mr-2 mb-3">
                                            <input key="u_question_09"
                                                name="u_question_09"
                                                type="radio"
                                                checked={item.value === question09}
                                                onChange={handleChange}
                                                value={item.value}
                                                className="h-[16px] w-[16px] border-gray-300 text-yellow focus:ring-0 focus:ring-offset-0 mr-1" />
                                            <span className="font-bold">{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            )}

            {question08 && question08 === "yes" && (
                <div className={questionRowClassName}>
                    <div className="w-[202px] flex justify-start items-baseline gap-3 shrink-0">
                        <TypeOptionsBlock>Histórico</TypeOptionsBlock>
                    </div>
                    <div data-testid="test-toggle-button" className="mt-7 block min-h-[1.5rem] pl-[1.5rem] min-w-0 flex-1">
                        <div className="z-20">
                            <legend className="font-semibold text-xs mb-3">
                                Indivíduo possui histórico de atos inseguros?
                            </legend>
                            <div
                                data-testid="test-u_question_10-container"
                                className='flex items-center'>
                                {yesOrNoOptions.map((item) => (
                                    <div key={`u_question_10-${item.id}`} className="mr-2 mb-3">
                                        <input key="u_question_10"
                                            name="u_question_10"
                                            type="radio"
                                            checked={item.value === question10}
                                            onChange={handleChange}
                                            value={item.value}
                                            className="h-[16px] w-[16px] border-gray-300 text-yellow focus:ring-0 focus:ring-offset-0 mr-1" />
                                        <span className="font-bold">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {(question10 && question10 === "yes") && (
                <div className={questionRowClassName}>
                    <div className="w-[202px] flex justify-start items-baseline gap-3 shrink-0">
                        <TypeOptionsBlock>Teste de substituição</TypeOptionsBlock>
                    </div>
                    <div data-testid="test-toggle-button" className="mt-7 block min-h-[1.5rem] pl-[1.5rem] min-w-0 flex-1">
                        <div className="z-20">
                            <legend className="font-semibold text-xs mb-3">
                                Há falhas de treinamento do indivíduo ou ele era inexperiente?
                            </legend>
                            <div
                                data-testid="test-u_question_09-container"
                                className='flex items-center'>
                                {yesOrNoOptions.map((item) => (
                                    <div key={`u_question_09b-${item.id}`} className="mr-2 mb-3">
                                        <input key="u_question_09"
                                            name="u_question_09"
                                            type="radio"
                                            checked={item.value === question09}
                                            onChange={handleChange}
                                            value={item.value}
                                            className="h-[16px] w-[16px] border-gray-300 text-yellow focus:ring-0 focus:ring-offset-0 mr-1" />
                                        <span className="font-bold">{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {result && (
                <div id='result' className="flex flex-row mt-4 flex-wrap items-center gap-y-2" >
                    <div className="mr-3 text-[#474747]/80 shrink-0">Resultado</div>
                    <>
                        <div className={`${getResult(result)?.style ?? ''} max-w-full`}>
                            <span className="text-white text-sm">
                                {getResult(result)?.description}
                            </span>
                        </div>
                    </>
                </div>
            )}
        </div>
    )
}
